// The ground under the results sheets' heatmap, on the light theme.
//
// The sheet's cells are the chart's first colour at an alpha that
// grows with the value, and what they composite over decides how the
// sheet reads. On the dark theme that ground is black and the ramp is
// right; on the light theme black sat on the page like a block, and
// white washed the ramp out. What reads well is a near-black cut from
// the palette's own anchor — the first chart colour — so the sheet is
// the deepest shade of the theme it belongs to: alice's blue over a
// midnight navy, ruby's red over a black-cherry.
//
// The ground is a CSS variable on the root, `--sheet-ground`, read by
// the container rule in assets/ag.css; App.vue keeps it in step with
// the palette whenever rawchartColors changes.

export const SHEET_GROUND_VAR = '--sheet-ground'

function rgbOf(hex) {
    const h = String(hex || '').replace('#', '')
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h.slice(0, 6)
    if (full.length !== 6 || /[^0-9a-f]/i.test(full)) return null
    return [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16))
}

function toHsl([r, g, b]) {
    r /= 255; g /= 255; b /= 255
    const max = Math.max(r, g, b), min = Math.min(r, g, b)
    const l = (max + min) / 2
    if (max === min) return [0, 0, l]
    const d = max - min
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
    return [h * 60, s, l]
}

function fromHsl([h, s, l]) {
    const k = n => (n + h / 30) % 12
    const a = s * Math.min(l, 1 - l)
    const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))
    return [f(0), f(8), f(4)].map(v => Math.round(v * 255))
}

const hexOf = rgb => '#' + rgb.map(c => ('0' + Math.max(0, Math.min(255, c)).toString(16)).slice(-2)).join('')

/**
 * The light-theme ground for a palette anchored on `hex`: the same
 * hue nudged a little cooler, fully saturated, and taken down to
 * near-black — the cells' alpha ramp only reads on the light page
 * when the ground is this deep. Alice's #1f78b4 comes out #00132e,
 * the hand-picked #00132f.
 */
export function groundFor(hex) {
    const rgb = rgbOf(hex)
    if (!rgb) return '#00132f'
    const [h, , l] = toHsl(rgb)
    const lightness = Math.min(0.11, Math.max(0.07, l * 0.22))
    return hexOf(fromHsl([(h + 12) % 360, 1, lightness]))
}

/** Set the root variable from a palette (its first colour is the anchor). */
export function applySheetGround(colors) {
    const anchor = Array.isArray(colors) && colors.length ? colors[0] : null
    if (typeof document === 'undefined') return
    document.documentElement.style.setProperty(SHEET_GROUND_VAR, groundFor(anchor))
}
